import { useState } from "react";
import { ReactComponent as LockIcon } from "@app/assets/svgs/Lock.svg";
import { passwordChange as passwordChangePromise } from "../api";
import { CircularProgress, Avatar, CssBaseline, FormControl, Input, InputAdornment } from "@material-ui/core";
import Dialog from "@app/common/components/material_ui/Dialog";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import commonWords from "@app/common/data/DjangoCommonPasswords";
import { trackEvent } from "@amplio-common/analytics";
import CommonButton from "@app/common/components/material_ui/Button";
import { Trans } from "@lingui/macro";
import { css } from "@emotion/react";
import PasswordValidate from "./password-validate";
import SupportDetails from "@app/common/components/SupportDetails";
import StreamlineIconWrapper from "@app/common/components/StreamlineIconWrapper";
import { toast } from "react-toastify";
import t from "prop-types";
import { PASSWORD_MIN_LENGTH, PASSWORD_COMPLEXITY_REG_EXP } from "../constants";
import { BOTTOM_PANNEL_DIALOG_Z_INDEX } from "@app/constants";


const rootStyle = theme => css`
  .MuiDialog-paper{
    border-radius: 16px;
  }
  .MuiInputBase-root{
    font-family: ${theme.typography.fontFamily};
  }
  .MuiFormLabel-root{
    font-family: ${theme.typography.fontFamily};
  }
  .MuiPaper-rounded {
      border-radius: 16px;
  }
  .MuiInputAdornment-root {
    cursor: pointer;
    color: #666;
  }
  .MuiFormControl-marginNormal {
      margin: max(1vh, 8px) 0;
  }
  .MuiAvatar-root > span > svg > path {
    stroke: ${theme.colors.lightText};
  }
  .MuiDialogActions-root {
    padding: 0;
  }
  .h6 {
    color: ${theme.colors.text.main};
  }
  .buttonWrapper {
    padding-bottom: 16px;
  }
  .dialog-main-root {
    font-family: ${theme.typography.fontFamily};
    width: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    @media (min-width: 553px) {
      width: 488px;
      margin-left: auto;
      margin-right: auto;
    }
  }
  .avatar {
    margin: auto;
    background-color: #F1F6FD;
  }
`;

const propTypes = {
  user: t.object,
  closeDialog: t.func,
};

const PasswordResetDialogComponent = ({ user, closeDialog }) => {
  const [isLoadingReset, setIsLoadingReset] = useState(false);
  const [stateErr, setStateErr] = useState(null);
  const [isShowOldPassword, setIsShowOldPassword] = useState(false);
  const [isShowNewPassword, setIsShowNewPassword] = useState(false);
  const [isShowNewPasswordConfirm, setIsShowNewPasswordConfirm] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  const _handleSubmit = (e) => {
    e.preventDefault();

    setIsLoadingReset(true);

    passwordChangePromise(oldPassword, newPassword, newPasswordConfirm).then(
      () => {
        trackEvent("create_new_password", { was_success: true, issue_description: "" });
        toast.success(<Trans>Password Changed Successfully</Trans>);
        closeDialog();
      },
    ).catch((err) => {
      const message = err && err.new_password ? err.new_password : ["An error has occured"];
      trackEvent("create_new_password", { was_success: false, issue_description: message });
      setStateErr(message);
      console.error(err);
    }).finally(
      () => setIsLoadingReset(false),
    );
  };

  let ErrorMessage = null;
  if (stateErr) {
    ErrorMessage = stateErr.map((errorMessage, index) => (
      <div key={`state-errDiag${index + 1}`} style={{ color: "red", textAlign: "center" }}>
        {errorMessage}
      </div>
    ));
  }

  const isOkPW = newPassword && newPassword.length >= PASSWORD_MIN_LENGTH
    && newPassword.match(PASSWORD_COMPLEXITY_REG_EXP)
    && !commonWords.includes(newPassword)
    && newPasswordConfirm && newPassword === newPasswordConfirm;

  return (
    <Dialog
      open
      maxWidth="lg"
      onClose={closeDialog}
      css={rootStyle}
      scroll="body"
      style={{ zIndex: BOTTOM_PANNEL_DIALOG_Z_INDEX }}
      content={(
        <main className="dialog-main-root">
          <CssBaseline />
          <Avatar className="avatar">
            <StreamlineIconWrapper><LockIcon /></StreamlineIconWrapper>
          </Avatar>
          <div className="h6">
            <Trans>Set a New Password</Trans>
          </div>

          <div style={{ textAlign: "center", margin: "10px 30px" }}>
            <Trans>Your data security is highly valued at Amplio.
              In order to keep your information safe, your password needs to be changed every 90 days. Please set your new password.
            </Trans>
          </div>

          <form onSubmit={_handleSubmit}>
            <FormControl margin="normal" required fullWidth>
              <Input
                placeholder={<Trans>Username</Trans>}
                className="user-name-input"
                id="username"
                name="username"
                type="text"
                autoComplete="off"
                autoFocus
                readOnly
                disabled
                value={user.username}
              />
            </FormControl>

            <FormControl margin="normal" required fullWidth>
              <Trans render={trans => (
                <Input
                  id="password"
                  name="password"
                  placeholder={trans.translation}
                  type={isShowOldPassword ? "text" : "password"}
                  autoComplete="off"
                  onChange={(e) => setOldPassword(e.target.value)}
                  autoFocus
                  endAdornment={(
                    <InputAdornment position="end" onClick={() => setIsShowOldPassword((prev) => !prev)}>
                      {isShowOldPassword ? <Visibility size={10} /> : <VisibilityOff size={10} />}
                    </InputAdornment>
                    )}
                />
              )}
              >Old Password
              </Trans>
            </FormControl>

            <FormControl margin="normal" required fullWidth>
              <Trans render={trans => (
                <Input
                  id="new_password"
                  placeholder={trans.translation}
                  name="new_password"
                  type={isShowNewPassword ? "text" : "password"}
                  autoComplete="off"
                  onChange={(e) => setNewPassword(e.target.value)}
                  endAdornment={(
                    <InputAdornment position="end" onClick={() => setIsShowNewPassword((prev) => !prev)}>
                      {isShowNewPassword ? <Visibility size={10} /> : <VisibilityOff size={10} />}
                    </InputAdornment>
                    )}
                />
              )}
              >New Password
              </Trans>
            </FormControl>

            <FormControl margin="normal" required fullWidth>
              <Trans render={trans => (
                <Input
                  id="re_new_password"
                  name="re_new_password"
                  placeholder={trans.translation}
                  type={isShowNewPasswordConfirm ? "text" : "password"}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  autoComplete="off"
                  endAdornment={(
                    <InputAdornment position="end" onClick={() => setIsShowNewPasswordConfirm((prev) => !prev)}>
                      {isShowNewPasswordConfirm ? <Visibility size={10} /> : <VisibilityOff size={10} />}
                    </InputAdornment>
                  )}
                />
              )}
              >Re-type New Password
              </Trans>
            </FormControl>

            {ErrorMessage}

            <div>
              <br />
              <PasswordValidate password={newPassword} passwordConfirm={newPasswordConfirm} />
              <br />
            </div>

            <div className="buttonWrapper">
              <CommonButton
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                color="primary"
                disabled={isLoadingReset || !isOkPW}
              >
                {isLoadingReset ? (<CircularProgress size={24} />) : (<Trans>Save password</Trans>)}
              </CommonButton>
            </div>
          </form>
          <SupportDetails showEmail={false} pageName="reset request" />
        </main>
      )}
    />
  );
};

PasswordResetDialogComponent.propTypes = propTypes;

export default PasswordResetDialogComponent;
